import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faSpinner,
  faNoteSticky,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import axiosInstance from "../utils/axiosInstance";
import GetUser from "../utils/getUser";
import { toast } from "react-toastify";
import { ParseErrors } from "../utils/ParseErrors";

interface INotesModel {
  onToggle: () => void;
  data: {
    policyNumber: string;
    /** Optional — shown in the header so the reader can confirm the right record. */
    customerName?: string;
  };
}

interface Message {
  branch: string;
  clientNumber: number;
  policyLine: number;
  policyReference: string;
  createdOn: string;
  agent: {
    name: string;
    code: string;
  };
  notes: string;
}

// ----------------------- NOTE HTML TO PLAIN TEXT, LINE BREAKS KEPT ------------------------------------
const noteText = (html: string) =>
  new DOMParser().parseFromString(
    (html || "").replace(/<br\s*\/?>|<\/p>|<\/div>/gi, "\n"),
    "text/html"
  ).body.textContent || "";

/** Initials for the avatar — falls back to the agent code, then to a dash. */
const getInitials = (name?: string, code?: string) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (code || "–").slice(0, 2).toUpperCase();
};

/** "Today" / "Yesterday" / "12 Mar 2025" — a date stamp on every note is noise. */
const getDayLabel = (date: string) => {
  const day = moment(date).startOf("day");
  const today = moment().startOf("day");
  const diff = today.diff(day, "days");
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return day.format("DD MMM YYYY");
};

const NotesModal = ({ onToggle, data }: INotesModel) => {
  const user = GetUser();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState<{
    totalCount: number;
    data: Message[];
  }>({ totalCount: 0, data: [] });
  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [newMessage, setNewMessage] = useState("");
  /** Set while older notes are being prepended, so the view can hold its place. */
  const restoreScroll = useRef<number | null>(null);

  const hasOlder = messages.data.length < messages.totalCount;

  //  ------------------------------------------------------------------------------------ HANDLE SEND MESSAGE -------------------------------------------------------------------------------------

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      const res = await axiosInstance.post(`/notes/${data?.policyNumber}`, {
        notes: newMessage,
      });
      setMessages((prev) => ({
        totalCount: prev.totalCount + 1,
        data: [...prev.data, res.data.data],
      }));
      setNewMessage("");
      setSending(false);
    } catch (error) {
      console.log(error);
      // The note was not saved — silently clearing the box would lose what was typed.
      toast.error(ParseErrors(error));
      setSending(false);
    }
  };

  //  ------------------------------------------------------------------------------------ FIND NOTES -------------------------------------------------------------------------------------

  useEffect(() => {
    const findNotes = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(
          `/notes/${data?.policyNumber}?page=${page}&limit=${limit}`
        );
        setMessages(res.data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
        toast.error(ParseErrors(error));
      }
    };

    data?.policyNumber && !messages.totalCount && findNotes();
  }, [data?.policyNumber, limit, page]);

  // ------------------------------------------------------------------------------------ LOAD EARLIER NOTES ------------------------------------------------------------------------------------
  // `page` is an offset into the history, so a higher offset returns *older* notes.
  // They are prepended, which is why the button reads "earlier" rather than "recent".
  const loadEarlierMessages = async () => {
    setLoading(true);
    restoreScroll.current = scrollRef.current?.scrollHeight ?? null;
    try {
      const res = await axiosInstance.get(
        `/notes/${data?.policyNumber}?page=${page + limit}&limit=${limit}`
      );
      setPage(page + limit);
      setMessages((prev) => ({
        totalCount: res.data.totalCount,
        data: [...res.data.data, ...prev.data],
      }));
      setLoading(false);
    } catch (error) {
      console.log(error);
      restoreScroll.current = null;
      setLoading(false);
      toast.error(ParseErrors(error));
    }
  };

  // -------------------------------------------------- SCROLL POSITION ----------------------------------------------------------------
  // Newest notes sit at the bottom, so land there on open and after sending. When
  // older notes are prepended instead, hold the reader where they were rather than
  // yanking them to the end of a list they just scrolled away from.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (restoreScroll.current !== null) {
      el.scrollTop = el.scrollHeight - restoreScroll.current;
      restoreScroll.current = null;
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [messages.data.length]);

  // ------------------------------------------ SCROLL LOCK + ESC TO CLOSE -----------------------------------------------
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onToggle();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onToggle]);

  const isFirstLoad = loading && !messages.data.length;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Policy notes"
      className="w-screen h-screen fixed inset-0 bg-[color:var(--shadow-modal-scrim)] backdrop-blur-sm flex items-center justify-center p-4 z-[99999] animate-scrim-in"
      onClick={onToggle}
    >
      <div
        className="w-full bg-surface border border-edge shadow-overlay rounded-2xl max-w-[620px] h-[80vh] flex flex-col overflow-hidden animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/*---------------------------------------------------------------------------------- Notes Header -------------------------------------------------------------------------------------*/}
        <div className="shrink-0 flex items-start justify-between gap-4 py-4 px-5 border-b border-edge bg-surface">
          <div className="min-w-0">
            <h2 className="text-[20px] font-bold text-text-primary leading-tight">
              Policy notes
            </h2>
            {/* Names the record and how much history there is, so the reader can tell
                an empty thread from one that simply has not loaded. */}
            <p className="mt-1 text-[13px] font-medium text-light-gray truncate">
              {[
                data?.customerName,
                data?.policyNumber,
                messages.totalCount
                  ? `${messages.totalCount} note${
                      messages.totalCount === 1 ? "" : "s"
                    }`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>

          <button
            type="button"
            onClick={onToggle}
            aria-label="Close"
            title="Close"
            className="shrink-0 h-9 w-9 rounded-lg flex items-center justify-center text-light-gray hover:text-text-primary hover:bg-overlay-hover transition-colors duration-200 cursor-pointer"
          >
            <FontAwesomeIcon icon={faXmark} className="text-[17px]" />
          </button>
        </div>

        {/*---------------------------------------------------------------------------------- Messages -------------------------------------------------------------------------------------*/}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4"
        >
          {isFirstLoad ? (
            <div className="h-full w-full flex flex-col items-center justify-center gap-3 text-light-gray">
              <FontAwesomeIcon icon={faSpinner} spin className="text-[26px]" />
              <p className="text-[13px] font-medium">Loading notes…</p>
            </div>
          ) : null}

          {!isFirstLoad && !messages.data.length ? (
            <div className="h-full w-full flex flex-col items-center justify-center gap-3 px-8 text-center">
              <span className="h-12 w-12 rounded-full bg-overlay-mid flex items-center justify-center text-light-gray">
                <FontAwesomeIcon icon={faNoteSticky} className="text-[18px]" />
              </span>
              <p className="text-[15px] font-semibold text-text-primary">
                No notes yet
              </p>
              <p className="text-[13px] font-medium text-light-gray leading-relaxed">
                Notes added here are written to the policy diary and are visible
                to everyone working this chase.
              </p>
            </div>
          ) : null}

          {messages.data.length ? (
            <>
              {hasOlder ? (
                <div className="flex justify-center mb-4">
                  <button
                    type="button"
                    className="text-[12px] font-semibold text-light-gray hover:text-text-primary bg-surface-elevated border border-edge hover:border-edge-strong rounded-full px-4 py-1.5 flex items-center gap-2 transition-colors duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={loadEarlierMessages}
                    disabled={loading}
                  >
                    {loading ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : null}
                    Load earlier notes
                  </button>
                </div>
              ) : null}

              {messages.data.map((msg, index) => {
                const isSender =
                  msg.agent?.code === user?.code || msg.agent?.code === "DS";
                // Date is shown once per day rather than under every bubble.
                const prev = messages.data[index - 1];
                const showDay =
                  !prev ||
                  !moment(prev.createdOn).isSame(moment(msg.createdOn), "day");

                return (
                  <div key={index}>
                    {showDay ? (
                      <div className="flex items-center gap-3 my-4">
                        <span className="h-px flex-1 bg-edge-faint" />
                        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-light-gray">
                          {getDayLabel(msg.createdOn)}
                        </span>
                        <span className="h-px flex-1 bg-edge-faint" />
                      </div>
                    ) : null}

                    <div
                      className={clsx(
                        "flex items-end gap-2.5 mb-3",
                        isSender ? "justify-end" : "justify-start"
                      )}
                    >
                      {!isSender ? (
                        <span
                          className="shrink-0 h-8 w-8 rounded-full bg-overlay-mid border border-edge-faint flex items-center justify-center text-[11px] font-bold text-text-secondary"
                          title={msg.agent?.name}
                        >
                          {getInitials(msg.agent?.name, msg.agent?.code)}
                        </span>
                      ) : null}

                      <div
                        className={clsx(
                          "max-w-[76%] min-w-0",
                          isSender ? "items-end" : "items-start"
                        )}
                      >
                        {!isSender && msg.agent?.name ? (
                          <p className="mb-1 text-[11px] font-semibold text-text-secondary">
                            {msg.agent.name}
                          </p>
                        ) : null}

                        <div
                          className={clsx(
                            "py-2.5 px-3.5 text-[13px] leading-relaxed rounded-2xl break-words [&_a]:underline",
                            isSender
                              ? "bg-gradient-to-br from-accent to-accent-bright text-white rounded-br-md shadow-glow-sm"
                              : "bg-overlay-mid border border-edge-faint text-text-primary rounded-bl-md"
                          )}
                        >
                          <div className="space-y-2 whitespace-pre-line">
                            {noteText(msg.notes)}
                          </div>
                        </div>

                        {/* Time only — the day is already on the separator above. */}
                        <p
                          className={clsx(
                            "mt-1 text-[11px] text-light-gray tabular-nums",
                            isSender ? "text-right" : "text-left"
                          )}
                        >
                          {moment(msg.createdOn).format("HH:mm")}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          ) : null}
        </div>

        {/*---------------------------------------------------------------------------------- Composer -------------------------------------------------------------------------------------*/}
        <form
          className="shrink-0 p-4 bg-surface-elevated border-t border-edge flex items-center gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
        >
          <input
            disabled={sending}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            aria-label="Write a note"
            className="flex-1 min-w-0 bg-input-bg h-11 text-[14px] px-4 border border-input-border focus:border-indigo-400/40 focus:ring-1 focus:ring-indigo-400/30 focus:outline-none transition-all duration-200 text-text-primary placeholder:text-label-muted rounded-xl disabled:opacity-60"
            placeholder="Write a note…"
          />

          {/* Submitting the form means Enter sends, which is what a message box implies. */}
          <button
            type="submit"
            title="Send note"
            aria-label="Send note"
            className="shrink-0 h-11 w-11 rounded-xl flex items-center justify-center text-white bg-gradient-to-r from-accent to-accent-bright border border-white/10 shadow-lg shadow-indigo-500/20 hover:shadow-glow-sm hover:-translate-y-0.5 transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-lg"
            disabled={sending || !newMessage.trim()}
          >
            <FontAwesomeIcon
              icon={sending ? faSpinner : faPaperPlane}
              spin={sending}
              className="text-[15px]"
            />
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default NotesModal;
